import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import Button from './Button'
import Modal from './Modal'
import { AddCreationsAuthor, EditCreation, EditCreations } from '../graphql/mutations'
import { GetAuthorRoles, GetCreationTypes, SearchSocieties, SearchUsers } from '../graphql/queries'
import SelectInput from './SelectInput'
import Input from './Input'
import DurationInput from './DurationInput'
import CreatableInput from './CreatableInput'
import { getSocietyCode } from './Authors'

const initialState = {
    title: '',
    originalTitle: '',
    episodeTitle: '',
    country: '',
    year: '',
    producer: '',
    season: null,
    episode: null,
    duration: '',
    type: '',
}

const authorState = {
    authorName: null,
    authorRole: null,
    ipi: '',
    authorSociety: null,
}

const initialErrorsState = {
    title: null,
    originalTitle: null,
    country: null,
    year: null,
    producer: null,
    season: null,
    episode: null,
    duration: null,
    type: null,
    authorName: null,
    authorRole: null,
    generic: null,
}

const isFilled = (value) => value !== '' && value !== null && value !== undefined

const EditCreationModal = ({
    show,
    close,
    onSuccess,
    initialData,
}) => {

    const { t } = useTranslation()
    const [item, setItem] = useState(initialState)
    const [items, setItems] = useState([])
    const [author, setAuthor] = useState(authorState)
    const [saving, setSaving] = useState(false)
    const [editCreation] = useMutation(EditCreation)
    const [editCreations] = useMutation(EditCreations)
    const [addCreationsAuthor] = useMutation(AddCreationsAuthor)

    const [errors, setErrors] = useState(initialErrorsState)

    const { data: typeData } = useQuery(GetCreationTypes)
    const { data: roleData } = useQuery(GetAuthorRoles)
    const roles = roleData?.getAuthorRoles ? roleData.getAuthorRoles : []

    const { refetch: searchUsers } = useQuery(SearchUsers, {
        skip: true,
    })

    const { refetch: searchSocieties } = useQuery(SearchSocieties, {
        skip: true,
    })

    useEffect(() => {
        if (initialData?.title) {
            setItem(initialData)
            return
        }

        if (initialData?.length && initialData[0]?.title) {
            setItems(initialData)
            return
        }

        setItem(initialState)
    }, [initialData])

    const getFieldData = () => {
        const data = {}
        if (item.type) data.type = parseInt(item.type)
        if (item.title?.trim()) data.title = item.title.trim()
        if (item.originalTitle?.trim()) data.originalTitle = item.originalTitle.trim()
        if (isFilled(item.year)) data.year = parseInt(item.year)
        if (isFilled(item.duration) && item.duration > 0) data.duration = parseInt(item.duration)
        return data
    }

    const getAuthorName = () => author?.authorName?.label || author?.authorName?.name || ''

    const hasAuthor = () => !!getAuthorName().trim() || !!author.authorRole

    const handleUpdate = async () => {
        if (saving || hasInputErrors()) return

        setSaving(true)
        try {
            const data = getFieldData()
            const ids = items?.length ? items.map((i) => parseInt(i.id)) : [parseInt(item?.id)]

            if (Object.keys(data).length > 0) {
                const res = !items?.length ? await editCreation({
                    variables: {
                        id: ids[0],
                        data,
                    },
                }) : await editCreations({
                    variables: {
                        ids,
                        data,
                    },
                })

                const result = res?.data?.editCreation || res?.data?.editCreations
                if (!result || result === 'FAIL') {
                    setErrors({
                        ...initialErrorsState,
                        generic: t('creations.update_error'),
                    })
                    return
                }
            }

            if (hasAuthor()) {
                const res = await addCreationsAuthor({
                    variables: {
                        ids,
                        data: {
                            authorName: getAuthorName().trim(),
                            authorRole: parseInt(author.authorRole),
                            ipi: author.ipi ? `${author.ipi}` : null,
                            authorSociety: getSocietyCode(author.authorSociety),
                        },
                    },
                })

                if (!res?.data?.addCreationsAuthor || res.data.addCreationsAuthor === 'FAIL') {
                    setErrors({
                        ...initialErrorsState,
                        generic: t('creations.add_author_error'),
                    })
                    return
                }
            }

            setItem(initialState)
            setAuthor(authorState)
            if (onSuccess) onSuccess('SUCCESS')
        } catch (err) {
            setErrors({
                ...initialErrorsState,
                generic: t('creations.update_error'),
            })
            console.log('handleUpdate:', err)
        } finally {
            setSaving(false)
        }
    }

    const hasInputErrors = () => {
        let hasErrors = false
        const errorState = {
            ...initialErrorsState,
        }

        const hasFields = Object.keys(getFieldData()).length > 0

        if (!hasFields && !hasAuthor()) {
            hasErrors = true
            errorState.generic = t('creations.fill_at_least_one_field')
        }

        if (isFilled(item.year) && (isNaN(parseInt(item.year)) || parseInt(item.year) < 1800 || parseInt(item.year) > 2100)) {
            hasErrors = true
            errorState.year = t('creations.invalid_year')
        }

        if (hasAuthor()) {
            if (!getAuthorName().trim()) {
                hasErrors = true
                errorState.authorName = t('reports.fill_field')
            }

            if (!author.authorRole) {
                hasErrors = true
                errorState.authorRole = t('reports.fill_field')
            }
        }

        setErrors({
            ...errorState,
        })
        return hasErrors
    }

    const handleClose = () => {
        if (close) close()
        setErrors(initialErrorsState)
        setItem(initialState)
        setAuthor(authorState)
    }

    const loadUsers = async (input) => {
        if (input.length < 2) return
        const res = await searchUsers({
            input,
        })
        return res?.data?.searchUsers
    }

    const loadSocieties = async (input) => {
        const res = await searchSocieties({
            input,
        })
        return res?.data?.searchSocieties
    }

    const setAuthorField = (field, value) => {
        setErrors({
            ...errors,
            [field]: null,
        })
        setAuthor({
            ...author,
            [field]: value,
        })
    }

    const handleSelectAuthor = (option) => {
        setErrors({
            ...errors,
            authorName: null,
        })

        if (!option) {
            setAuthor({
                ...author,
                authorName: null,
                ipi: '',
            })
            return
        }

        setAuthor({
            ...author,
            authorName: option,
            ipi: option.__isNew__ ? '' : (option.ipi || ''),
        })
    }

    const renderActions = () => (
        <div className='modal-actions'>
            <Button
                label={t('creations.save')}
                onClick={handleUpdate}
                loading={saving}
                disabled={saving}
            />
        </div>
    )

    return (
        <Modal
            className={'modal-edit-creation'}
            show={show}
            close={handleClose}
            title={t('creations.update_selected_creations')}
            renderActions={renderActions}
        >
            <p>{items?.length > 1 ? t('creations.selected_creations', { count: items?.length }) : t('creations.selected_creation')}</p>
            <Input
                label={t('creations.creation_title')}
                value={item.title}
                onChange={(e) => setItem({ ...item, title: e.target.value })}
                error={errors.title}
            />
            <Input
                label={t('creations.original_title')}
                value={item.originalTitle}
                onChange={(e) => setItem({ ...item, originalTitle: e.target.value })}
                error={errors.originalTitle}
            />
            <Input
                label={t('creations.year')}
                value={item.year}
                onChange={(e) => setItem({ ...item, year: e.target.value })}
                type={'number'}
                error={errors.year}
            />
            <DurationInput
                label={t('creations.duration')}
                onChange={(seconds) => setItem({ ...item, duration: seconds > 0 ? seconds : '' })}
                error={errors.duration}
            />
            <SelectInput
                label={t('creations.type')}
                options={typeData?.getCreationTypes ? typeData.getCreationTypes : []}
                getOptionLabel={(option) => t(option.name)}
                getOptionValue={(option) => option.id}
                onChange={(option) => setItem({ ...item, type: option.id })}
                error={errors.type}
            />

            <div className='modal-edit-creation--author'>
                <h4>{ t('creation.add_author') }</h4>
                <CreatableInput
                    placeholder={t('creation.author_name')}
                    load={loadUsers}
                    value={author.authorName}
                    getOptionLabel={(option) => option.name || option.label}
                    getOptionValue={(option) => option.id || option.value}
                    onChange={handleSelectAuthor}
                    loadingMessage={() => t('users.loading_users')}
                    noOptionsMessage={() => t('users.type_to_search')}
                    isClearable={true}
                    error={errors.authorName}
                    menuPortalTarget={document.body}
                />
                <Input
                    label={t('creation.author_ipi')}
                    value={author.ipi}
                    type={'number'}
                    onChange={(e) => setAuthorField('ipi', e.target.value)}
                />
                <SelectInput
                    placeholder={t('creation.author_role')}
                    options={roles}
                    value={author.authorRole ? roles.find(x => x.id === author.authorRole) : null}
                    getOptionLabel={(option) => t(option.name)}
                    getOptionValue={(option) => option.id}
                    onChange={(option) => setAuthorField('authorRole', option.id)}
                    error={errors.authorRole}
                />
                <CreatableInput
                    getOptionLabel={(option) => option.name || option.label}
                    getOptionValue={(option) => option.code || option.value}
                    onChange={(option) => setAuthorField('authorSociety', option)}
                    isClearable={true}
                    value={author.authorSociety}
                    placeholder={t('filters.society')}
                    label={t('creations.society_optional')}
                    load={loadSocieties}
                    loadingMessage={() => t('societies.loading')}
                    noOptionsMessage={() => t('societies.type_to_search')}
                    menuPortalTarget={document.body}
                />
            </div>

            {
                errors.generic ?
                    <div className='error-message'>
                        <span>{errors.generic}</span>
                    </div>
                    :
                    <></>
            }
        </Modal>
    )
}

export default EditCreationModal
